import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { CreditCard, Check, Clock, AlertCircle, Download } from 'lucide-react'
import api from '../config/api'
import { useI18n } from '../hooks/useI18n'
import '../styles/PaymentHistory.css'

function PaymentHistory() {
  const { t } = useI18n()
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchPayments()
  }, [])

  const fetchPayments = async () => {
    try {
      const response = await api.get('/payment-history')
      setPayments(response.data.data || [])
    } catch (err) {
      console.error('Failed to load payments:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency', currency: 'PHP', minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatDate = (date) => {
    if (!date) return '-'
    const d = date._seconds ? new Date(date._seconds * 1000) : new Date(date)
    return d.toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await api.get(`/payments/${paymentId}/receipt`, { responseType: 'blob' })
      const blob = new Blob([response.data], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 10000)
    } catch (err) {
      console.error('Failed to download receipt:', err)
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': case 'paid': return <Check size={14} className="status-icon success" />
      case 'pending': return <Clock size={14} className="status-icon warning" />
      case 'failed': return <AlertCircle size={14} className="status-icon danger" />
      default: return <CreditCard size={14} className="status-icon" />
    }
  }

  if (loading) {
    return <div className="payment-loading"><div className="loading-spinner" /><p>{t('common.loading')}</p></div>
  }

  const totalPaid = payments
    .filter(p => p.status === 'completed' || p.status === 'paid')
    .reduce((sum, p) => sum + (p.amount || 0), 0)

  return (
    <div className="payment-history-page">
      <div className="page-header">
        <h1>Payment History</h1>
        <p>Your payment transactions</p>
      </div>

      <motion.div className="payment-summary" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="payment-summary-item">
          <span className="payment-summary-label">Total Payments</span>
          <span className="payment-summary-value">{payments.length}</span>
        </div>
        <div className="payment-summary-item">
          <span className="payment-summary-label">Total Paid</span>
          <span className="payment-summary-value primary">{formatCurrency(totalPaid)}</span>
        </div>
      </motion.div>

      {payments.length === 0 ? (
        <div className="payment-empty">No payment history found</div>
      ) : (
        <div className="payment-list">
          {payments.map((payment, index) => (
            <motion.div key={payment.id} className="payment-item"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}>
              <div className="payment-item-left">
                <div className="payment-item-status">
                  {getStatusIcon(payment.status)}
                  <span className={`payment-status-text ${payment.status}`}>{payment.status}</span>
                </div>
                <div className="payment-item-details">
                  {payment.invoiceNumber && <span className="payment-invoice">Invoice: {payment.invoiceNumber}</span>}
                  {payment.method && <span className="payment-method">{payment.method}</span>}
                  {payment.reference && <span className="payment-ref">Ref: {payment.reference}</span>}
                </div>
              </div>
              <div className="payment-item-right">
                <div className="payment-amount">{formatCurrency(payment.amount)}</div>
                <div className="payment-date">{formatDate(payment.date)}</div>
                {(payment.status === 'completed' || payment.status === 'paid') && (
                  <button className="receipt-btn" onClick={() => handleDownloadReceipt(payment.id)} title="View Receipt">
                    <Download size={14} /> Receipt
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}

export default PaymentHistory
